import React, { useState } from 'react';

export default function LoginForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotMsg, setForgotMsg] = useState("");

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch('https://server.oracy.uk/server/registration/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
  
      const payload = await res.json();
      if (!res.ok) {
        throw new Error(payload.message || 'Login failed');
      }
  
      // on success, navigate to the assessment app
      window.location.href = 'https://dashboard.oracy.uk';
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async e => {
    e.preventDefault();
    setForgotSubmitting(true);
    setForgotMsg("");
    try {
      const res = await fetch("https://server.oracy.uk/server/registration/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const payload = await res.json();
      setForgotMsg(payload.message || "If that email is registered, a reset link has been sent.");
    } catch (err) {
      setForgotMsg("Something went wrong. Please try again later.");
    } finally {
      setForgotSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-orange-100 py-16 px-4">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-10 sm:p-12 flex flex-col items-center transition-transform duration-200 hover:scale-[1.025] hover:shadow-[0_8px_40px_rgba(247,148,29,0.15)]">
        {/* Logo/Icon */}
        <div className="mb-8 flex flex-col items-center">
          <img src="/Logodark.svg" alt="Oracy Logo" className="h-20 w-20 mb-3" />
          <h2 className="text-4xl font-extrabold text-theme-orange mb-2 tracking-tight">Welcome Back</h2>
          <p className="text-gray-500 text-base">Sign in to your Oracy account</p>
        </div>
        <form onSubmit={handleSubmit} className="w-full mt-2">
          {error && <div className="mb-4 text-red-600 text-sm text-center font-medium">{error}</div>}
          <div className="mb-6">
            <label className="block mb-2 text-base font-medium text-gray-700">Email</label>
            <input
              type="email"
              className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-500 transition text-base"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="you@example.com"
            />
          </div>
          <div className="mb-8">
            <label className="block mb-2 text-base font-medium text-gray-700">Password</label>
            <input
              type="password"
              className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-500 transition text-base"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="Your password"
            />
            <div className="text-right mt-2">
              <button
                type="button"
                className="text-orange-600 text-sm font-semibold hover:underline focus:outline-none"
                onClick={() => setShowForgot(v => !v)}
              >
                Forgot Password?
              </button>
            </div>
          </div>
          {showForgot && (
            <div className="w-full mb-6 flex flex-col items-center bg-orange-50 rounded-lg p-4 border border-orange-200">
              <label className="block mb-2 text-base font-medium text-gray-700 w-full text-left">Enter your email to reset password</label>
              <input
                type="email"
                className="w-full border border-gray-300 rounded-lg p-3 mb-2 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-500 transition text-base"
                value={forgotEmail}
                onChange={e => setForgotEmail(e.target.value)}
                required
                placeholder="you@example.com"
                disabled={forgotSubmitting}
              />
              <button
                type="button"
                className="w-full bg-orange-600 text-white py-2 rounded-lg font-semibold text-base shadow hover:bg-orange-700 transition disabled:opacity-60"
                disabled={forgotSubmitting}
                onClick={handleForgotPassword}
              >
                {forgotSubmitting ? "Sending..." : "Send Reset Link"}
              </button>
              {forgotMsg && (
                <div className="mt-2 text-sm text-center text-gray-700">
                  {forgotMsg.includes("reset link has been sent")
                    ? "If that email is registered, a reset link has been sent. Please check your email, reset your password, and come back to login."
                    : forgotMsg}
                </div>
              )}
            </div>
          )}
          <button
            type="submit"
            className="w-full bg-orange-600 text-white py-3 rounded-lg font-semibold text-lg shadow hover:bg-orange-700 transition disabled:opacity-60 mb-2"
            disabled={submitting}
          >
            {submitting ? 'Logging in...' : 'Login'}
          </button>
        </form>
        <div className="mt-8 text-base text-gray-600">
          Don't have an account?{' '}
          <a href="/register" className="text-orange-600 font-semibold hover:underline">Register</a>
        </div>
      </div>
    </div>
  );
} 